import { openDB, type DBSchema, type IDBPDatabase } from "idb";

/**
 * Durable local storage for offline-first support - the foundation Phase 1
 * (queueing real mutations) and Phase 2 (read-side reference caching) build
 * on. Nothing writes to this yet; today's "offline" writes still live only
 * in the in-memory Zustand store (see domain/store.ts's reporterOffline/
 * pendingSync) and are lost on refresh. This file exists so that fix has
 * somewhere durable to land.
 *
 * `pendingActions` - the future mutation queue. Each row is one queued
 * write (raise ask, submit evidence, etc.) with enough to replay it and
 * track its outcome without guessing: never auto-retried past `failed` and
 * never silently dropped - a failed sync keeps its payload and the server's
 * rejection reason for a "needs attention" view, per the product decision
 * that financial/compliance data must never resolve conflicts silently.
 *
 * `cachedReference` - read-side data (projects/milestones/budgets) cached
 * for offline viewing, each entry stamped with when it was cached so the UI
 * can show real staleness ("cached 3h ago") instead of pretending offline
 * data is current.
 */

export type PendingActionStatus = "pending" | "syncing" | "synced" | "failed";

export interface PendingAction {
  id: string;
  /** Identifies which mutation to replay - e.g. "raiseSpend", "submitEvidence". */
  type: string;
  payload: unknown;
  createdAt: number;
  status: PendingActionStatus;
  /** Set only on status "failed" - the server's own rejection reason, shown
   * verbatim so the user can judge whether to edit-and-resubmit or discard,
   * never auto-retried on their behalf. */
  lastError: string | null;
}

export interface CachedReferenceEntry {
  key: string;
  data: unknown;
  cachedAt: number;
}

interface OfflineDbSchema extends DBSchema {
  pendingActions: {
    key: string;
    value: PendingAction;
    indexes: { "by-status": PendingActionStatus };
  };
  cachedReference: {
    key: string;
    value: CachedReferenceEntry;
  };
}

const DB_NAME = "il-offline";
const DB_VERSION = 1;

let dbPromise: Promise<IDBPDatabase<OfflineDbSchema>> | null = null;

export function getOfflineDb(): Promise<IDBPDatabase<OfflineDbSchema>> {
  if (!dbPromise) {
    dbPromise = openDB<OfflineDbSchema>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        const actions = db.createObjectStore("pendingActions", { keyPath: "id" });
        actions.createIndex("by-status", "status");
        db.createObjectStore("cachedReference", { keyPath: "key" });
      },
    });
  }
  return dbPromise;
}

/** Durably cache one piece of read-only reference data (e.g. the workspace
 * seed) so it survives a reload with zero connectivity - see
 * SeedProvider.tsx, the first real consumer of this store. */
export async function cacheReference(key: string, data: unknown): Promise<void> {
  const db = await getOfflineDb();
  await db.put("cachedReference", { key, data, cachedAt: Date.now() });
}

export async function getCachedReference(key: string): Promise<CachedReferenceEntry | undefined> {
  const db = await getOfflineDb();
  return db.get("cachedReference", key);
}

/** Wraps a queryFn with SeedProvider's own pattern (cache a successful
 * result, fall back to the last cached copy if the fetch fails - typically
 * offline) so a TanStack Query hook gets the same offline resilience
 * without hand-rolling the fetch-then-cache-fallback dance at every call
 * site. Callers should also set `networkMode: "always"` on the query -
 * otherwise TanStack Query's default `networkMode: "online"` pauses the
 * query (never calls queryFn at all) whenever the browser reports offline
 * and there's no data already in this tab's in-memory cache, which would
 * mean this fallback never gets a chance to run in the exact case it
 * exists for (a fresh reload while offline). */
export async function fetchWithOfflineCache<T>(key: string, fetcher: () => Promise<T>): Promise<T> {
  try {
    const data = await fetcher();
    void cacheReference(key, data);
    return data;
  } catch (err) {
    const cached = await getCachedReference(key);
    if (cached) return cached.data as T;
    throw err;
  }
}
